import {ReactElement, useState, useEffect, useContext, Fragment} from "react"
import {ItemWarehouse} from "./api/itemwarehouse"
import {WarehouseTransferLine} from "./api/warehousetransfer"
import {SalesOrderLine} from "./api/salesorder"
import {PurchaseOrderLine} from "./api/purchaseorder"
import {ShippingAggregationButtons} from "./ShippingAggregationButtons"
import "./PASDetailedBody.scss"
import {ISODate} from "./date"
import {getLevelAndCustomLevelMessage, getAvailble } from "./PASOverviewTableRow"
import {SlideOpenClose} from "./SlideOpenClose"
import { Link } from "react-router-dom";
import { useDivision} from "./shared"
import {AllDataContext} from "./LoggedinMainPage"

type Props = {
  itemWarehouse: ItemWarehouse
  infoLevel: number
  warningLevel: number
}

export function PASDetailedBody(
    {itemWarehouse, infoLevel, warningLevel}: Props
): ReactElement {
  const [openDates, setOpenDates] = useState(new Set<ISODate>())
  const division = useDivision()

  useEffect(() => {
    setOpenDates(new Set())
    // may keep sourcesById; it's by uuid, so should not interfere between items
  }, [itemWarehouse])

  const allData = useContext(AllDataContext)

  const projectedAvailableStock = allData.projectedStocks.filter(
    ps => ps.warehouseCode === itemWarehouse.warehouseCode
    && ps.itemCode === itemWarehouse.itemCode)[0]


  function copySetToggleMember<T>(set: Set<T>, member: T) {
    const newSet = new Set(set)
    if (set.has(member)) {
      newSet.delete(member)
    } else {
      newSet.add(member)
    }
    return newSet
  }

  const getWarehouseLink = (warehouseCode: string): string =>  {
      return [
        "",
        "division",
        division,
        "PASDetailed",
        warehouseCode,
        itemWarehouse.itemCode,
        "0" + warningLevel,
        "0" + infoLevel,
      ].map(encodeURIComponent).join("/")
  }

  const getLevel = (stock: number | undefined) =>
    getLevelAndCustomLevelMessage(stock, warningLevel, infoLevel, false, false)[0]

  const today = ISODate.localToday()

  return <div className="PASDetailedBody">
    <div className="header">
      <div className={"item " + getLevel(
        projectedAvailableStock === undefined ? undefined
        : projectedAvailableStock.currentAvailableStock)}>
        <span className="code">{itemWarehouse.itemCode}</span>
        <span className="description">{itemWarehouse.itemDescription}</span>
      </div>
      <div className="warehouseCode">{itemWarehouse.warehouseCode}</div>
    </div>
    <div className="stock">
      <table className="overview">
        <tbody>
        <tr>
          <th>Current availble stock at {itemWarehouse.warehouseCode}</th>
          <td className={getLevel(projectedAvailableStock.currentAvailableStock)}>
            {projectedAvailableStock.currentAvailableStock}
          </td>
        </tr>
        <tr>
          <th>Current real stock at {itemWarehouse.warehouseCode}</th>
          <td>{projectedAvailableStock.currentRealStock}</td>
        </tr>
        <tr>
          <th>Availability</th>
          <td>{getAvailble(projectedAvailableStock, infoLevel)}</td>
        </tr>
        <tr>
          <th>Warning level</th>
          <td>{warningLevel}</td>
        </tr>
        <tr>
          <th>Info level</th>
          <td>{infoLevel}</td>
        </tr>
        <tr>
          <th>Total planned in</th>
          <td>{itemWarehouse.plannedStockIn}</td>
        </tr>
        <tr>
          <th>Total planned out</th>
          <td>{itemWarehouse.plannedStockOut}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div><h2>Plans</h2>
      <table className="paslist">
        <thead>
          <tr>
            <th>Date</th>
            <th>PAS</th>
            <th>PRS</th>
            <th className="openallbuttons">
              <button disabled={
                  openDates.size === projectedAvailableStock.projection.size}
                  onClick={() => setOpenDates(new Set(
                    projectedAvailableStock.projection.keys()))}>
                open all
              </button>
              /
              <button disabled={openDates.size === 0} onClick={
                () => setOpenDates(new Set())} >
                close all
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={getLevel(projectedAvailableStock.currentAvailableStock)}>
            <td className="date">In warehouse</td>
            <td className="pas">{projectedAvailableStock.currentAvailableStock}</td>
            <td className="prs">{projectedAvailableStock.currentRealStock}</td>
          </tr>

{Array.from(projectedAvailableStock.projection.entries(), ([date, ps]) => {
  return <Fragment key={date.toISOString()}>
  <tr onClick={() => setOpenDates(
            openDates => copySetToggleMember(openDates, date))}
            className={ps.date.lt(today) ? "error"
                       : getLevel(ps.eodProjectedAvailableStock)}>
      <td className="date">{date.toDDMMYYYYString()}</td>
      <td className="pas">{ps.eodProjectedAvailableStock}</td>
      <td className="prs">{ps.eodProjectedRealStock}</td>
      <td className="details">
        <SlideOpenClose isOpen={openDates.has(date)}>
          <div><table><tbody>
            {ps.iwpds.map(iwpd => {
              const source = iwpd.planningSource
              return <tr key={iwpd.getUniqueKey()}>
                <td className="diff">
                  {iwpd.plannedQuantity >= 0 && "+"}{iwpd.plannedQuantity}
                </td>
                <td className="sourcenr">
                  <a href={source.parent.getExactOnlineUrl(division).toString()}
                    target="_blank" rel="noreferrer">
                    {iwpd.getPlanningSourceTypeShort()}:
                    {"transferNumber" in source.parent
                      ? source.parent.transferNumber
                    : iwpd.getPlanningSourceNumber()}
                  </a>
                </td>
                <td className="priority">
                  {source instanceof SalesOrderLine && 
                    source.parent.$calc.priority}
                </td>
                <td className="delay">
                  {source instanceof SalesOrderLine && 
                    source.parent.deliveryDate.getDaysUntil(
                    source.deliveryDate)}
                </td>
                <td className="sourcedescription">
                  {"description" in source && source.description};
                  {"description" in source.parent && source.parent.description};
                </td>
                <td className="sourceparty">{iwpd.getOtherParty()}</td>
                <td className="shourceshipping">
                  {source instanceof PurchaseOrderLine
                    ? <ShippingAggregationButtons
                      shippingAggregates={source.$calc.shipping} />
                    : source instanceof WarehouseTransferLine
                      ? <>
                        {iwpd.getPlanningSourceType() === "WarehouseTransfer-out"
                          ? source.parent.warehouseFromDescription
                          : <Link
                            to={getWarehouseLink(source.parent.warehouseFromCode)}>
                            <button>
                              {source.parent.warehouseFromDescription}
                            </button>
                          </Link>}
                        {" "} &#x2192; {" "}
                        {iwpd.getPlanningSourceType() === "WarehouseTransfer-in"
                          ? source.parent.warehouseToDescription
                          : <Link
                            to={getWarehouseLink(source.parent.warehouseToCode)}>
                            <button>
                              {source.parent.warehouseToDescription}
                            </button>
                          </Link>}
                      </>
                      : ""
                  }</td>
              </tr>
            })
            }
          </tbody></table></div>
        </SlideOpenClose>
        <hr />
        <div className="opener">&nbsp;</div>
      </td>
  </tr>
  </Fragment>
          })}
        </tbody>
      </table>
    </div>
  </div>
}
